import { AccountPlan, Subscription } from '@/constants/enum';
import { IPricingPlanData, IUserPlan } from '@/types/dashboard';
import dayjs from 'dayjs';

export interface IRangeQuota {
  min: number;
  max: number;
}

export { sortedPlansByTier } from '@/constants/plan';

export const UNLIMITED_QUOTA = 'unlimited';

export const getAllUniqueQuotaValues = (pricingPlans: IPricingPlanData[]): number[] => {
  const quotaArray: number[] = [];

  pricingPlans.forEach((plan) => {
    // Add base quota per month
    const baseQuota = parseInt(plan.quotaPerMonth);
    if (!quotaArray.includes(baseQuota)) {
      quotaArray.push(baseQuota);
    }

    // Add all additional quota amounts
    plan.extraQuota.forEach((extra) => {
      const totalQuota = parseInt(plan.quotaPerMonth) + extra.additionalAmount;
      if (!quotaArray.includes(totalQuota)) {
        quotaArray.push(totalQuota);
      }
    });
  });

  // Sort and return
  return quotaArray.sort((a, b) => a - b);
};

export const getMonthlyQuantity = (plan: AccountPlan, subscription: Subscription) => {
  return plan !== AccountPlan.Starter && subscription === Subscription.Annually ? 12 : 1;
};

export const getRangeQuotaForAllPlans = (allPlans: IPricingPlanData[], activeSubscription: Subscription) => {
  return allPlans.reduce<Record<AccountPlan, IRangeQuota>>((acc, curr) => {
    const minQuotaPerMonth = parseInt(curr.quotaPerMonth, 10);
    const maxQuotaPerMonth = curr.maxQuotaPerMonth;
    const monthQuantity = getMonthlyQuantity(curr.plan as AccountPlan, activeSubscription);

    acc[curr.plan as AccountPlan] = {
      min: minQuotaPerMonth * monthQuantity,
      max: maxQuotaPerMonth * monthQuantity,
    };

    return acc;
  }, {} as Record<AccountPlan, IRangeQuota>);
};

export const getExtraQuotaFromTotalQuotaPerMonth = (
  plan: AccountPlan,
  subscription: Subscription,
  quotaRange: Record<AccountPlan, IRangeQuota>,
  totalQuota: number,
) => {
  const { min, max } = quotaRange[plan];
  const monthQuantity = getMonthlyQuantity(plan, subscription);
  if (plan === AccountPlan.Starter || totalQuota <= min) {
    return 0;
  }
  if (totalQuota >= max) {
    return (max - min) / monthQuantity;
  }
  return (totalQuota - min) / monthQuantity;
};

export const isShopOnFreePlan = (userPlan?: Pick<IUserPlan, 'plan' | 'monthlyPricing'>): boolean => {
  const price = userPlan?.monthlyPricing?.[userPlan.plan];
  return price !== undefined && !(price > 0);
};

export const isShopMidTrial = (userPlan?: Pick<IUserPlan, 'plan' | 'monthlyPricing' | 'trialInfo'>): boolean =>
  userPlan?.trialInfo?.state === 'mid_trial' && !isShopOnFreePlan(userPlan);

export const formatTrialEndDate = (endsOn?: string | null) => (endsOn ? dayjs(endsOn).format('MMM D, YYYY') : '');

export const getSubscriptionText = (subscription: Subscription, replace: boolean = true, text: string = '') => {
  if (replace) return text ? text : subscription.replace(/ly$/, '');
  return subscription;
};
